import React, { FC, useState } from "react";
import { Factor, FACTORS_TO_SHOW } from "@app/service/user";
import { defineMessages, useIntl } from "react-intl";
import PreferenceDialogSvg from "@assets/svg/preference-dialog.svg";
import { Svg } from "@natera/material/lib/svg";
import { Button } from "@natera/form";
import { ScrollContext } from "@natera/platform/lib/components/scrollbar";
import FactorList from "@app/components/factorList/factorList";

import "./chooseSignInTwoFactorMethod.scss";
import { FactorsObject } from "@app/provider/mfaSetup";
import { GradientWrapper } from "../auth";

const messages = defineMessages({
  chooseSignInTwoFactorMethodTitle: {
    id: "chooseSignInTwoFactorMethodTitle",
    defaultMessage:
      "Which method would you like to use for two-factor authentication?",
  },
  chooseSignInTwoFactorMethodButton: {
    id: "chooseSignInTwoFactorMethodButton",
    defaultMessage: "Send code",
  },
  chooseSignInTwoFactorMethodGoBackButton: {
    id: "chooseSignInTwoFactorMethodGoBackButton",
    defaultMessage: "Go back",
  },
});

interface ChooseSignInTwoFactorMethodProps extends React.HTMLAttributes<HTMLDivElement> {
  factors: Factor[];
  onSubmitFactor: (factor?: Factor) => void;
  goBack?: () => void;
}

const ChooseSignInTwoFactorMethod: FC<ChooseSignInTwoFactorMethodProps> = ({
  factors,
  onSubmitFactor,
  goBack,
}) => {
  const intl = useIntl();
  const [selectedFactor, setSelectedFactor] = useState<Factor>();

  const prepareFactorsObject = () => {
    const factorObj: FactorsObject = {};
    factors.forEach((factor) => (factorObj[factor.factorType] = factor));
    return factorObj;
  };

  const getFactorPhoneNumber = () => {
    const factorWithPhone = factors.find((factor) =>
      [FACTORS_TO_SHOW.sms, FACTORS_TO_SHOW.call].includes(factor.factorType)
    );
    return factorWithPhone?.profile?.phoneNumber;
  };

  const getFactorEmail = () => {
    const factorWithEmail = factors.find((factor) =>
      [FACTORS_TO_SHOW.email].includes(factor.factorType)
    );
    return factorWithEmail?.profile?.email;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    onSubmitFactor(selectedFactor);
  };

  return (
    <div className="choose-two-factor-method">
      <GradientWrapper display={true} disableForMobile={true}>
        <ScrollContext
          component="div"
          isStatic={false}
          settings={{ suppressScrollX: true }}
        >
          <div className="choose-two-factor-method__container">
            <div className="choose-two-factor-method__icon">
              <Svg
                className="preference-dialog-icon"
                content={PreferenceDialogSvg}
              />
            </div>
            <form onSubmit={handleSubmit}>
              <h2>
                {intl.formatMessage(messages.chooseSignInTwoFactorMethodTitle)}
              </h2>
              <FactorList
                profile={{
                  email: getFactorEmail(),
                  phone: getFactorPhoneNumber(),
                }}
                factorsObject={prepareFactorsObject()}
                setSelectedFactor={setSelectedFactor}
                selectedFactorType={selectedFactor?.factorType}
                isLoginFlow={true}
              />
              <div className="choose-two-factor-method__footer">
                <Button unelevated disabled={!selectedFactor} type="submit">
                  {intl.formatMessage(
                    messages.chooseSignInTwoFactorMethodButton
                  )}
                </Button>
                {goBack && (
                  <Button outlined onClick={goBack} type="button">
                    {intl.formatMessage(
                      messages.chooseSignInTwoFactorMethodGoBackButton
                    )}
                  </Button>
                )}
              </div>
            </form>
          </div>
        </ScrollContext>
      </GradientWrapper>
    </div>
  );
};

export default ChooseSignInTwoFactorMethod;
